// inv/src/components/ui/inputNumber.jsx
import { forwardRef, useEffect, useState } from "react";
import { cn } from "../../lib/utils";

const parseAmount = (input) => {
  if (!input) return null;
  const inputStr = typeof input === "string" ? input : String(input);
  const regex = /^([\d,.]+)\s*([kKmMbBtT])?$/;
  const match = inputStr.trim().match(regex);
  if (match) {
    let value = parseFloat(match[1].replace(/,/g, ""));
    const multiplier = match[2] ? match[2].toLowerCase() : null;
    switch (multiplier) {
      case "k":
        value *= 1e3;
        break;
      case "m":
        value *= 1e6;
        break;
      case "b":
        value *= 1e9;
        break;
      case "t":
        value *= 1e12;
        break;
      default:
        break;
    }
    return value;
  }
  return null;
};

const formatAmount = (amount) => {
  if (amount == null || amount === "") return "";
  if (amount >= 1e12) return (amount / 1e12).toString() + "t";
  if (amount >= 1e9) return (amount / 1e9).toString() + "b";
  if (amount >= 1e6) return (amount / 1e6).toString() + "m";
  if (amount >= 1e3) return (amount / 1e3).toString() + "k";
  return amount.toString();
};

const InputNumber = forwardRef(
  (
    {
      value,
      onChange,
      className,
      placeholder = "",
      allowNegative = false,
      title,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState(
      value !== null && value !== undefined ? formatAmount(value) : ""
    );
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
      if (!isEditing) {
        setInternalValue(
          value !== null && value !== undefined ? formatAmount(value) : ""
        );
      }
    }, [value, isEditing]);

    const handleChange = (e) => {
      const inputValue = e.target.value;

      const validPattern = allowNegative
        ? /^-?[\d.,kKmMbBtT]*$/
        : /^[\d.,kKmMbBtT]*$/;

      if (!validPattern.test(inputValue)) return;

      setInternalValue(inputValue);

      if (onChange) {
        const parsedValue = parseAmount(inputValue);
        onChange(parsedValue);
      }
    };

    const handleBlur = () => {
      setIsEditing(false);

      if (!internalValue.trim()) {
        setInternalValue("");
        onChange && onChange(null);
        return;
      }

      const parsedValue = parseAmount(internalValue);
      if (parsedValue !== null) {
        setInternalValue(formatAmount(parsedValue));
        onChange && onChange(parsedValue);
      } else {
        setInternalValue("");
        onChange && onChange(null);
      }
    };

    const handleFocus = () => {
      setIsEditing(true);
    };

    return (
      <div
        title={title}
        className={cn(
          "flex h-8 cursor-pointer items-center rounded-md border border-stone-300 bg-transparent px-2 py-1 shadow-sm transition-colors focus-within:ring-1 focus-within:ring-stone-200",
          className
        )}
        style={{
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        <input
          ref={ref}
          type="text"
          title={title}
          className="flex-1 cursor-pointer bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          inputMode="decimal"
          spellCheck={false}
          style={{
            minWidth: "80px",
            flexGrow: 1,
            height: "1.6rem",
            overflowX: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "clip",
          }}
          {...props}
        />
      </div>
    );
  }
);

InputNumber.displayName = "InputNumber";

export { InputNumber };
